/**
 * Recharge skip_charge endpoint returns only the onetime item of a charge even there are subscription items in it.
 * If a charge has all subscription items with same frequency then it will return all the subscription items. Otherwise it returns only one item.
 * For this reason we are handling the tracking events in this file. Otherwise we have handled other events in the SelectedItemContext file
 */
import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/Button";
import {
  useSelectedItemContext,
  useSkipCharge,
  useFetchSubscriptions,
  useSkipSubscription,
} from "../../SelectedItemContext";
import { DrawerPageProps } from "../types";
import { Box } from "../../BoxSchedule";
import { ItemCard } from "../../SubProductCard";
import { productSelect } from "@/store";
import {
  droppsProductToProductCardProduct,
  getDisplayDate,
} from "../../helpers";
import { PortalDrawer, EditUpcomingOrderDrawer } from "../index";
import { DelayShipmentPage } from "./DelayItemPage";
import {
  fetchCharges,
  updateBulkOneTimeProduct,
} from "../../RechargeApiActions";
import { addMonthsToDate } from "@/helpers/utils";
import { track } from "@/helpers/googleAnalytics";

export interface SkipShipmentPageProps extends DrawerPageProps {
  box?: Box;
}
const SkipShipmentPage = ({ notify, box, onExit }: SkipShipmentPageProps) => {
  const { rechargeItem, product, variant } = useSelectedItemContext().boxItem;
  const [openedDrawer, setOpenedDrawer] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const productCardProduct = droppsProductToProductCardProduct({
    product,
    variant,
  });
  const skipChargeProcessor = useSkipCharge();
  const skipSubscriptionProcessor = useSkipSubscription();
  const getSubscriptions = useFetchSubscriptions();
  const chargeIdRef = useRef<null | number>(null);
  const futureSkipDate = addMonthsToDate(
    rechargeItem.nextChargeDate,
    rechargeItem?.cadence?.frequency || 0
  );
  const activeSubs = box?.items.filter(
    (item) => item.rechargeItem.status === "ACTIVE"
  );
  activeSubs?.sort(
    (a, b) =>
      (a.rechargeItem.cadence?.frequency || 0) -
      (b.rechargeItem.cadence?.frequency || 0)
  );
  const itemWithLeastFrequency = activeSubs && activeSubs[0];
  const leastFrequency =
    itemWithLeastFrequency?.rechargeItem.cadence?.frequency;
  const oneTimeDelayDate = addMonthsToDate(
    itemWithLeastFrequency?.rechargeItem.nextChargeDate ?? new Date(),
    leastFrequency ?? 1
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const charges = await fetchCharges({ subscriptionId: rechargeItem.id });
        if (charges.length > 0) {
          chargeIdRef.current = charges[0].id;
        }
      } catch (error) {
        notify({
          title: "Error",
          message: "Error on fetching charges",
        });
      }
    };
    fetchData();
  }, [notify, rechargeItem]);

  const handleSkip = async () => {
    setLoading(true);
    if (box && chargeIdRef.current) {
      const { items } = box;
      const hasOneTime = items.some(
        (boxItem) => boxItem.rechargeItem.status === "ONETIME"
      );
      const lineItems = items.map((item) => item.rechargeItem);
      if (!hasOneTime) {
        const subscriptionIds = items.map((item) => item.rechargeItem.id);
        skipChargeProcessor.mutate(
          {
            chargeId: chargeIdRef.current,
            subscriptionIds,
          },
          {
            onSuccess() {
              setLoading(false);
              notify({
                title: "Box Skipped",
                message: `Your shipping date is now ${getDisplayDate(
                  futureSkipDate
                )}`,
              });
              lineItems.length &&
                track({ name: "Portal Skip Order", items: lineItems });
              getSubscriptions.mutate();
              onExit();
            },
            onError() {
              setLoading(false);
            },
          }
        );
      } else {
        const oneTimeIDs = items
          .filter((item) => item.rechargeItem.status === "ONETIME")
          .map((item) => item.rechargeItem.id);
        const subscriptionIds = items
          .filter((item) => item.rechargeItem.status !== "ONETIME")
          .map((item) => item.rechargeItem.id);
        const oneTimeRequestObj = oneTimeIDs.map((oneTimeID) => {
          return { orderId: oneTimeID, nextChargeDate: oneTimeDelayDate };
        });
        skipChargeProcessor.mutate(
          {
            chargeId: chargeIdRef.current,
            subscriptionIds,
          },
          {
            onSuccess() {
              updateBulkOneTimeProduct(oneTimeRequestObj)
                .then((results) => {
                  setLoading(false);
                  getSubscriptions.mutate();
                  notify({
                    title: "Box Skipped",
                    message: `Your shipping date is now ${getDisplayDate(
                      futureSkipDate
                    )}`,
                  });
                  lineItems.length &&
                    track({ name: "Portal Skip Order", items: lineItems });
                  onExit();
                })
                .catch((error) => {
                  setLoading(false);
                  console.error("Error during requests:", error);
                });
            },
            onError() {
              setLoading(false);
            },
          }
        );
      }
    } else {
      skipSubscriptionProcessor.mutate(rechargeItem.id, {
        onSuccess() {
          setLoading(false);
          notify({
            title: "Product Skipped",
            message: `Your shipping date is now ${getDisplayDate(
              futureSkipDate
            )}`,
          });
          track({ name: "Portal Skip Order", items: [rechargeItem] });
          getSubscriptions.mutate();
          onExit();
        },
      });
    }
  };

  return (
    <>
      <div className="w-full space-y-4">
        <h3 className="subtitle text-base font-semibold">
          {box ? "Products to be Skipped" : "item to be Skipped"}
        </h3>
        <div className="space-y-4">
          {box ? (
            box.items.map((s) => {
              const { product, variant, rechargeItem } = s;
              const productCardProduct = droppsProductToProductCardProduct({
                product,
                variant,
              });
              return (
                <ItemCard
                  key={rechargeItem.id}
                  product={productCardProduct}
                  price={rechargeItem.price}
                  quantity={rechargeItem.quantity}
                  shippingInfo={rechargeItem.cadence ?? undefined}
                  className="rounded border border-solid border-primary"
                  isSubBadge={true}
                />
              );
            })
          ) : (
            <ItemCard
              key={product.id}
              product={productCardProduct}
              price={rechargeItem.price}
              quantity={rechargeItem.quantity}
              shippingInfo={rechargeItem.cadence ?? undefined}
              className="rounded border border-solid border-primary"
              isSubBadge={true}
            />
          )}
        </div>
      </div>
      <div className="w-full space-y-4">
        <p className="subtitle text-base font-semibold">NEW CHARGE DATE</p>
        {(box && box.items.length === 1) || !box ? (
          <p className="body text-sm">
            Skipping your box will push your charge date by{" "}
            {`${
              rechargeItem?.cadence?.frequency
            } ${rechargeItem?.cadence?.unit.getUnitForValue(
              rechargeItem?.cadence.frequency
            )}`}{" "}
            to:{" "}
            <span>
              {getDisplayDate(
                addMonthsToDate(
                  rechargeItem.nextChargeDate,
                  rechargeItem.cadence?.frequency || 0
                )
              )}
            </span>
          </p>
        ) : null}

        {box && box?.items?.length > 1 && (
          <>
            <p className="body text-sm">
              Skipping your box will push your charge date to:{" "}
            </p>
            <ul className="ml-5 text-sm">
              {box?.items.map((item) => {
                const { rechargeItem, product, variant } = item;
                const productCardProduct = droppsProductToProductCardProduct({
                  product,
                  variant,
                });
                const updatedDate =
                  rechargeItem.status === "ACTIVE"
                    ? addMonthsToDate(
                        rechargeItem.nextChargeDate,
                        rechargeItem.cadence?.frequency || 0
                      )
                    : oneTimeDelayDate;
                return (
                  <li
                    key={rechargeItem.id + rechargeItem.shopify_product_id}
                    className="list-disc"
                  >
                    {productSelect.name(productCardProduct)}:{" "}
                    {getDisplayDate(updatedDate)}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <Button
          className="w-full"
          loading={loading}
          onClick={handleSkip}
          data-portal-button="confirm-skip-shipment"
        >
          Confirm Skip
        </Button>
        <Button
          className="w-full"
          variant="outline"
          onClick={() => {
            setOpenedDrawer(true);
          }}
        >
          Delay Instead
        </Button>
      </div>
      {
        <PortalDrawer
          open={openedDrawer}
          title={box ? "Delay This Box" : "Delay This Item"}
          contents={(nav) =>
            openedDrawer ? (
              box ? (
                <DelayShipmentPage
                  onExit={onExit}
                  shipment={box}
                  onBack={() => {}}
                  goToFirstPage={() => {}}
                  notify={() => {}}
                />
              ) : (
                <EditUpcomingOrderDrawer
                  page={{ kind: "delay-shipment" }}
                  {...nav}
                  nextChargeDate={rechargeItem.nextChargeDate}
                />
              )
            ) : null
          }
          notify={() => {}}
          onExit={onExit}
          goToFirstPage={() => {}}
          transparentBackdrop={true}
        />
      }
    </>
  );
};

export { SkipShipmentPage };
